import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, CornerDownLeft, Clock, AlertOctagon, Truck, MapPin, Cpu, Users, Code2, FileLock2 } from "lucide-react";
import { useSearch } from "../lib/queries";
import { SEVERITY_COLOR } from "../lib/format";
import type { SearchHit } from "../types/api";

const ENTITY_ICON: Record<string, typeof Search> = {
  event: AlertOctagon,
  asset: Truck,
  site: MapPin,
  device: Cpu,
  personnel: Users,
  model: Code2,
  audit: FileLock2,
};

const GROUPS: { key: keyof SearchResultsMap; label: string }[] = [
  { key: "events", label: "EXCEPTIONS" },
  { key: "assets", label: "ASSETS" },
  { key: "sites", label: "SITES" },
  { key: "devices", label: "DEVICES" },
  { key: "personnel", label: "PERSONNEL" },
  { key: "models", label: "MODELS" },
  { key: "audit", label: "AUDIT" },
];

type SearchResultsMap = {
  events: SearchHit[]; assets: SearchHit[]; sites: SearchHit[]; devices: SearchHit[];
  personnel: SearchHit[]; models: SearchHit[]; audit: SearchHit[];
};

const RECENT_KEY = "po.recent_searches";

export default function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState("");
  const { data, isFetching } = useSearch(q);
  const nav = useNavigate();
  const [recent, setRecent] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
  });

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const flat = useMemo(() => {
    if (!data) return [] as { hit: SearchHit; group: string }[];
    const out: { hit: SearchHit; group: string }[] = [];
    GROUPS.forEach((g) => (data.results[g.key] || []).forEach((hit) => out.push({ hit, group: g.key })));
    return out;
  }, [data]);

  function go(hit: SearchHit) {
    const next = [q, ...recent.filter((r) => r !== q)].filter(Boolean).slice(0, 6);
    setRecent(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    const routes: Record<string, string> = {
      event: `/monitor?case=${hit.id}`,
      asset: "/configure", site: "/configure", device: "/configure", personnel: "/configure",
      model: "/developer", audit: "/comply",
    };
    nav(routes[hit.entity] ?? "/monitor");
    onClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 backdrop-blur-sm pt-[12vh]" onClick={onClose}>
      <div className="glass w-full max-w-xl overflow-hidden !rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
          <Search size={18} className="text-aurora-cyan" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search exceptions, assets, sites, personnel, devices, models, audit…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-600"
          />
          <span className="mono rounded border border-white/10 px-1.5 py-0.5 text-[10px] text-slatey">ESC</span>
        </div>

        <div className="max-h-[55vh] overflow-y-auto p-2">
          {q.trim().length < 2 ? (
            <div className="p-3">
              <div className="section-tag mb-2">RECENT</div>
              {recent.length === 0 && <div className="text-xs text-slate-600">Type at least 2 characters…</div>}
              {recent.map((r) => (
                <button key={r} onClick={() => setQ(r)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-slatey hover:bg-white/5">
                  <Clock size={13} /> {r}
                </button>
              ))}
            </div>
          ) : flat.length === 0 ? (
            <div className="p-4 text-center text-sm text-slatey">{isFetching ? "Searching…" : "No matches."}</div>
          ) : (
            GROUPS.filter((g) => (data?.results[g.key] || []).length).map((g) => (
              <div key={g.key} className="mb-2">
                <div className="section-tag px-2 py-1">{g.label}</div>
                {(data!.results[g.key] || []).map((hit) => {
                  const Icon = ENTITY_ICON[hit.entity] ?? Search;
                  return (
                    <button
                      key={`${hit.entity}-${hit.id}`}
                      onClick={() => go(hit)}
                      className="group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-white/5"
                    >
                      <Icon size={15} style={{ color: hit.severity ? SEVERITY_COLOR[hit.severity] : "#22D3EE" }} />
                      <span className="flex-1 truncate text-sm">{hit.label}</span>
                      <span className="truncate text-xs text-slatey">{hit.sub}</span>
                      <CornerDownLeft size={13} className="text-slate-600 opacity-0 group-hover:opacity-100" />
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="border-t border-white/10 px-4 py-2 text-[10px] text-slate-600">
          {data ? `${data.total} results` : "Global search"} · drillable & deep-linkable
        </div>
      </div>
    </div>
  );
}
